import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import Webcam from 'react-webcam';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';

const VIN_BOX = {
    left: 0.10,
    top: 0.42,
    width: 0.88,
    height: 0.08,
};

const BARCODE_FORMATS = [
    BarcodeFormat.CODE_39,
    BarcodeFormat.CODE_128,
    BarcodeFormat.PDF_417,
    BarcodeFormat.DATA_MATRIX,
    BarcodeFormat.QR_CODE,
    BarcodeFormat.CODABAR,
    BarcodeFormat.ITF,
];

const NATIVE_BARCODE_FORMATS = [
    'code_39',
    'code_128',
    'pdf417',
    'data_matrix',
    'qr_code',
    'codabar',
    'itf',
];

const videoConstraints = {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1920, min: 1280 },
    height: { ideal: 1080, min: 720 },
    advanced: [
        { focusMode: 'continuous' },
        { exposureMode: 'continuous' },
        { whiteBalanceMode: 'continuous' },
    ],
};

function cleanVinText(value) {
    return String(value || '')
        .toUpperCase()
        .replace(/[IOQ]/g, (letter) => {
            if (letter === 'I') return '1';
            return '0';
        });
}

function extractVinFromBarcodeRaw(rawValue) {
    if (!rawValue) return null;

    const cleaned = cleanVinText(rawValue).replace(/[^A-Z0-9]/g, ' ');
    const matches = cleaned.match(/[A-HJ-NPR-Z0-9]{17}/g);

    if (!matches || !matches.length) {
        return null;
    }

    return matches[0];
}

function loadImageFromDataUrl(dataUrl) {
    return new Promise((resolve, reject) => {
        const image = new Image();

        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = dataUrl;
    });
}

const VinSmartScanner = ({
    onClose,
    onBarcodeDetected,
    onVinBoxCaptured,
    barcodeOnly = true,
}) => {
    const webcamRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const nativeScanCanvasRef = useRef(null);
    const scannerControlsRef = useRef(null);
    const nativeDetectorRef = useRef(null);
    const nativeScanTimerRef = useRef(null);
    const barcodeProcessingRef = useRef(false);
    const barcodeFoundRef = useRef(false);
    const torchTrackRef = useRef(null);

    const [cameraReady, setCameraReady] = useState(false);
    const [barcodeFound, setBarcodeFound] = useState(false);
    const [ocrMode, setOcrMode] = useState(false);
    const [isCapturing, setIsCapturing] = useState(false);
    const [capturedImage, setCapturedImage] = useState(null);
    const [barcodeStatus, setBarcodeStatus] = useState('Point camera at VIN barcode');
    const [isBarcodeScanning, setIsBarcodeScanning] = useState(true);
    const [scanEngine, setScanEngine] = useState('Starting camera scanner...');
    const [torchSupported, setTorchSupported] = useState(false);
    const [torchOn, setTorchOn] = useState(false);

    const stopNativeScanner = useCallback(() => {
        if (nativeScanTimerRef.current) {
            clearTimeout(nativeScanTimerRef.current);
            nativeScanTimerRef.current = null;
        }
    }, []);

    const stopZxingScanner = useCallback(() => {
        try {
            if (scannerControlsRef.current) {
                scannerControlsRef.current.stop();
                scannerControlsRef.current = null;
            }
        } catch (error) {
            console.warn('ZXing stop error:', error);
        }
    }, []);

    const stopScanner = useCallback(() => {
        stopNativeScanner();
        stopZxingScanner();
        setIsBarcodeScanning(false);
    }, [stopNativeScanner, stopZxingScanner]);

    const handleClose = useCallback(() => {
        stopScanner();
        onClose();
    }, [onClose, stopScanner]);

    const resetBarcodeScanning = useCallback(() => {
        barcodeProcessingRef.current = false;
        barcodeFoundRef.current = false;
        setBarcodeFound(false);
        setIsBarcodeScanning(true);
        setBarcodeStatus('Point camera at VIN barcode');
    }, []);

    const notifyBarcodeStatus = useCallback((type, message, description) => {
        setBarcodeStatus(description || message);
        const notify = notification[type] || notification.info;

        notify({
            message,
            description,
            duration: 3,
        });
    }, []);

    const processBarcodeRawValue = useCallback(async ({ rawValue, format, source }) => {
        if (barcodeProcessingRef.current || barcodeFoundRef.current) {
            return;
        }

        const vin = extractVinFromBarcodeRaw(rawValue);

        console.log('VIN barcode candidate:', {
            source,
            format,
            rawValue,
            vin,
        });

        if (!vin) {
            notifyBarcodeStatus(
                'warning',
                'VIN Decode Failed',
                'Barcode found, but VIN was not detected. Move closer to the VIN label.'
            );
            return;
        }

        barcodeProcessingRef.current = true;
        barcodeFoundRef.current = true;
        setBarcodeFound(true);
        setIsBarcodeScanning(false);
        notifyBarcodeStatus('info', 'VIN Barcode Found', `VIN found: ${vin}. Validating...`);

        stopScanner();

        try {
            const result = await onBarcodeDetected({
                vin,
                rawValue,
                format,
                source,
            });

            if (result?.success) {
                notifyBarcodeStatus('success', 'VIN Decode Successful', `VIN ${vin} autofilled`);
                onClose();
                return;
            }

            notifyBarcodeStatus(
                'warning',
                'VIN Decode Failed',
                'VIN was read, but backend could not decode it. Try scanning again.'
            );

            setTimeout(() => {
                resetBarcodeScanning();
                setCameraReady((value) => !value);
            }, 900);
        } catch (error) {
            console.warn('VIN barcode callback error:', error);
            notifyBarcodeStatus(
                'warning',
                'VIN Decode Failed',
                'VIN barcode failed. Try again.'
            );

            setTimeout(() => {
                resetBarcodeScanning();
                setCameraReady((value) => !value);
            }, 900);
        }
    }, [onBarcodeDetected, onClose, notifyBarcodeStatus, resetBarcodeScanning, stopScanner]);

    const setupTorchSupport = useCallback(() => {
        const video = webcamRef.current?.video;
        const stream = video?.srcObject;
        const track = stream?.getVideoTracks?.()[0];

        if (!track) {
            return;
        }

        torchTrackRef.current = track;

        const capabilities = track.getCapabilities?.();

        if (capabilities?.torch) {
            setTorchSupported(true);
        }
    }, []);

    const toggleTorch = async () => {
        const track = torchTrackRef.current;

        if (!track) {
            return;
        }

        try {
            await track.applyConstraints({
                advanced: [{ torch: !torchOn }],
            });

            setTorchOn(!torchOn);
        } catch (error) {
            console.warn('Torch toggle failed:', error);
            setTorchSupported(false);
        }
    };

    const startNativeBarcodeScanner = useCallback(async () => {
        if (!('BarcodeDetector' in window)) {
            return false;
        }

        try {
            const supportedFormats = await window.BarcodeDetector.getSupportedFormats?.();
            const formats = NATIVE_BARCODE_FORMATS.filter((format) => {
                if (!supportedFormats || !supportedFormats.length) return true;
                return supportedFormats.includes(format);
            });

            if (!formats.length) {
                return false;
            }

            nativeDetectorRef.current = new window.BarcodeDetector({ formats });
            setScanEngine('Native barcode scanner + ZXing fallback');

            const scanFrame = async () => {
                if (barcodeFoundRef.current || barcodeProcessingRef.current || ocrMode || capturedImage) {
                    return;
                }

                const video = webcamRef.current?.video;

                if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
                    nativeScanTimerRef.current = setTimeout(scanFrame, 250);
                    return;
                }

                try {
                    const canvas = nativeScanCanvasRef.current || document.createElement('canvas');
                    nativeScanCanvasRef.current = canvas;

                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;

                    const ctx = canvas.getContext('2d', { willReadFrequently: true });
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                    const results = await nativeDetectorRef.current.detect(canvas);

                    if (results?.length) {
                        const first = results[0];

                        await processBarcodeRawValue({
                            rawValue: first.rawValue || first.displayValue,
                            format: first.format,
                            source: 'native-barcode-detector',
                        });

                        return;
                    }
                } catch (error) {
                    console.warn('Native barcode scan error:', error);
                }

                nativeScanTimerRef.current = setTimeout(scanFrame, 220);
            };

            scanFrame();
            return true;
        } catch (error) {
            console.warn('Native BarcodeDetector start error:', error);
            return false;
        }
    }, [capturedImage, ocrMode, processBarcodeRawValue]);

    const startZxingBarcodeScanner = useCallback(async () => {
        try {
            const videoElement = webcamRef.current?.video;

            if (!videoElement) {
                return;
            }

            const hints = new Map();
            hints.set(DecodeHintType.POSSIBLE_FORMATS, BARCODE_FORMATS);
            hints.set(DecodeHintType.TRY_HARDER, true);
            hints.set(DecodeHintType.ALSO_INVERTED, true);
            hints.set(DecodeHintType.CHARACTER_SET, 'UTF-8');

            const codeReader = new BrowserMultiFormatReader(hints, {
                delayBetweenScanAttempts: 120,
                delayBetweenScanSuccess: 500,
            });

            setScanEngine((current) => current.includes('Native') ? current : 'ZXing barcode scanner');

            scannerControlsRef.current = await codeReader.decodeFromVideoElement(
                videoElement,
                (result) => {
                    if (barcodeFoundRef.current || barcodeProcessingRef.current || ocrMode || capturedImage) {
                        return;
                    }

                    if (!result) {
                        return;
                    }

                    processBarcodeRawValue({
                        rawValue: result.getText(),
                        format: result.getBarcodeFormat?.()?.toString?.(),
                        source: 'zxing',
                    });
                }
            );
        } catch (error) {
            console.warn('ZXing barcode scanner start error:', error);
            setIsBarcodeScanning(false);
            notifyBarcodeStatus(
                'warning',
                'Scanner Unavailable',
                'Barcode scanner not available. Use manual VIN entry.'
            );
        }
    }, [capturedImage, ocrMode, processBarcodeRawValue, notifyBarcodeStatus]);

    useEffect(() => {
        if (!cameraReady || barcodeFoundRef.current || ocrMode || capturedImage) {
            return undefined;
        }

        let cancelled = false;

        const start = async () => {
            setIsBarcodeScanning(true);
            notifyBarcodeStatus('info', 'VIN Scanner Ready', 'Point camera at VIN barcode');
            setupTorchSupport();

            const nativeStarted = await startNativeBarcodeScanner();

            if (cancelled) {
                return;
            }

            await startZxingBarcodeScanner();

            if (!nativeStarted) {
                setScanEngine('ZXing barcode scanner');
            }
        };

        start();

        return () => {
            cancelled = true;
            stopScanner();
        };
    }, [cameraReady, capturedImage, ocrMode, notifyBarcodeStatus, setupTorchSupport, startNativeBarcodeScanner, startZxingBarcodeScanner, stopScanner]);

    const captureVinBox = async () => {
        if (barcodeFoundRef.current || barcodeFound || barcodeOnly) {
            return;
        }

        const video = webcamRef.current?.video;

        if (!video) {
            return;
        }

        setIsCapturing(true);

        try {
            if (!video.videoWidth || !video.videoHeight || video.readyState < 2) {
                setIsCapturing(false);
                notifyBarcodeStatus(
                    'warning',
                    'Camera Not Ready',
                    'Camera is not ready. Please try again.'
                );
                return;
            }

            const staticImageUrl = webcamRef.current?.getScreenshot();

            if (!staticImageUrl) {
                setIsCapturing(false);
                notifyBarcodeStatus(
                    'warning',
                    'Capture Failed',
                    'Capture failed. Please try again.'
                );
                return;
            }

            const capturedFrame = await loadImageFromDataUrl(staticImageUrl);
            const imageWidth = capturedFrame.naturalWidth || capturedFrame.width;
            const imageHeight = capturedFrame.naturalHeight || capturedFrame.height;
            const previewCanvas = previewCanvasRef.current;

            if (previewCanvas) {
                previewCanvas.width = imageWidth;
                previewCanvas.height = imageHeight;

                const previewCtx = previewCanvas.getContext('2d');
                previewCtx.drawImage(capturedFrame, 0, 0, imageWidth, imageHeight);
            }

            const cropX = Math.round(imageWidth * VIN_BOX.left);
            const cropY = Math.round(imageHeight * VIN_BOX.top);
            const cropWidth = Math.round(imageWidth * VIN_BOX.width);
            const cropHeight = Math.round(imageHeight * VIN_BOX.height);

            const cropCanvas = document.createElement('canvas');
            cropCanvas.width = cropWidth;
            cropCanvas.height = cropHeight;

            const cropCtx = cropCanvas.getContext('2d');

            cropCtx.drawImage(
                capturedFrame,
                cropX,
                cropY,
                cropWidth,
                cropHeight,
                0,
                0,
                cropWidth,
                cropHeight
            );

            video.pause();
            setCapturedImage(staticImageUrl);
            notifyBarcodeStatus('info', 'Reading VIN Text', 'Reading VIN text...');
            stopScanner();

            cropCanvas.toBlob(
                async blob => {
                    if (!blob) {
                        setIsCapturing(false);
                        notifyBarcodeStatus(
                            'warning',
                            'Capture Failed',
                            'Capture failed. Please retake.'
                        );
                        return;
                    }

                    const file = new File([blob], 'vin-crop.jpg', {
                        type: 'image/jpeg',
                    });

                    try {
                        const result = await onVinBoxCaptured(file);

                        if (result?.success) {
                            notifyBarcodeStatus(
                                'success',
                                'VIN Decode Successful',
                                `VIN ${result.vin} autofilled`
                            );
                        } else {
                            notifyBarcodeStatus(
                                'warning',
                                'VIN Decode Failed',
                                'VIN text not detected. Please retake.'
                            );
                        }
                    } catch (error) {
                        console.warn('VIN OCR callback error:', error);
                        notifyBarcodeStatus(
                            'warning',
                            'VIN Decode Failed',
                            'VIN text not detected. Please retake.'
                        );
                    } finally {
                        setIsCapturing(false);
                    }
                },
                'image/jpeg',
                0.95
            );
        } catch (error) {
            console.warn('VIN box capture error:', error);
            setIsCapturing(false);
        }
    };

    const handleUseOcrMode = () => {
        if (barcodeFoundRef.current || barcodeFound || barcodeOnly) {
            return;
        }

        stopScanner();
        setOcrMode(true);
        notifyBarcodeStatus('info', 'VIN OCR Mode', 'Align VIN text inside the green box');
    };

    const handleRetake = () => {
        const video = webcamRef.current?.video;
        const previewCanvas = previewCanvasRef.current;

        if (video) {
            video.play().catch(error => {
                console.warn('Camera resume error:', error);
            });
        }

        if (previewCanvas) {
            previewCanvas.getContext('2d').clearRect(
                0,
                0,
                previewCanvas.width,
                previewCanvas.height
            );
        }

        setCapturedImage(null);
        setIsCapturing(false);
        setOcrMode(true);
        notifyBarcodeStatus('info', 'VIN OCR Mode', 'Align VIN text inside the green box');
    };

    return (
        <div className="vin-scanner-overlay">
            <div className="vin-scanner-header">
                <button
                    type="button"
                    className="vin-scanner-close"
                    onClick={handleClose}
                >
                    ×
                </button>

                <div className="text-center">
                    <h5 className="mb-0 text-white">Scan VIN Barcode</h5>

                    {!ocrMode && (
                        <small>Scan the barcode on the driver-side door jamb VIN label.</small>
                    )}

                    {ocrMode && (
                        <small>Place VIN text inside the green box.</small>
                    )}
                </div>
            </div>

            <div className="vin-camera-wrapper">
                <Webcam
                    ref={webcamRef}
                    audio={false}
                    mirrored={false}
                    playsInline={true}
                    screenshotFormat="image/jpeg"
                    screenshotQuality={0.95}
                    forceScreenshotSourceSize={true}
                    videoConstraints={videoConstraints}
                    onUserMedia={() => setCameraReady(true)}
                    onUserMediaError={error => {
                        console.warn('Camera error:', error);
                        setIsBarcodeScanning(false);
                        notifyBarcodeStatus(
                            'warning',
                            'Camera Error',
                            'Camera permission denied or unavailable'
                        );
                    }}
                    className={`vin-camera-video ${capturedImage ? 'vin-camera-video-hidden' : ''}`}
                />

                <canvas
                    ref={previewCanvasRef}
                    className={`vin-capture-preview ${capturedImage ? 'show' : ''}`}
                    aria-hidden="true"
                />

                {!ocrMode && !capturedImage && (
                    <div className="vin-barcode-guide">
                        <span>Fill barcode here</span>
                    </div>
                )}

                {ocrMode && !barcodeFound && !capturedImage && !barcodeOnly && (
                    <div className="vin-guide-box">
                        <span>Align VIN text here</span>
                    </div>
                )}
            </div>

            <div className="vin-scanner-footer">
                <div className="vin-scanner-status">
                    {!ocrMode && isBarcodeScanning && !barcodeFound && (
                        <span className="vin-spinner" />
                    )}

                    <span>{barcodeStatus}</span>
                </div>

                {!ocrMode && !capturedImage && (
                    <div className="vin-scanner-help">
                        <small>{scanEngine}</small>
                        <small>Hold steady, move closer slowly, avoid glare.</small>
                    </div>
                )}

                {torchSupported && !capturedImage && (
                    <button
                        type="button"
                        className="btn btn-outline-light w-100 mt-2"
                        onClick={toggleTorch}
                    >
                        {torchOn ? 'Turn Flash Off' : 'Turn Flash On'}
                    </button>
                )}

                {!barcodeOnly && !ocrMode && !capturedImage && (
                    <button
                        type="button"
                        className="btn btn-outline-light w-100 mt-2"
                        onClick={handleUseOcrMode}
                        disabled={barcodeFound}
                    >
                        No Barcode? Scan VIN Text
                    </button>
                )}

                {!barcodeOnly && ocrMode && !capturedImage && (
                    <button
                        type="button"
                        className="btn btn-success w-100"
                        onClick={captureVinBox}
                        disabled={isCapturing || barcodeFound}
                    >
                        {isCapturing ? 'Capturing...' : 'Capture VIN Text'}
                    </button>
                )}

                {capturedImage && (
                    <button
                        type="button"
                        className="btn btn-outline-light w-100"
                        onClick={handleRetake}
                        disabled={isCapturing}
                    >
                        Retake
                    </button>
                )}

                <button
                    type="button"
                    className="btn btn-outline-light w-100 mt-2"
                    onClick={handleClose}
                >
                    {capturedImage ? 'Done' : 'Cancel'}
                </button>
            </div>
        </div>
    );
};

export default VinSmartScanner;
